/**
 * Hybrid menu loading (Shopify Section Rendering API):
 * - Drawer panel loads on first open / prefetch
 * - Collection product grids load on hover / submenu open
 * @see https://shopify.dev/docs/storefronts/themes/best-practices/performance/reduce-mega-menu-dom-overhead
 * @see https://shopify.dev/docs/api/ajax/section-rendering
 */
(function () {
  var DRAWER_SECTION = 'menu-drawer-panel';
  var PRODUCTS_SECTION = 'mega-menu-products';
  var drawerLoaded = false;
  var drawerLoading = null;
  var productsCache = Object.create(null);

  function nextFrame(cb) {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(cb);
    } else {
      setTimeout(cb, 0);
    }
  }

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function withSectionsParam(url, sectionId) {
    try {
      var parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('sections', sectionId);
      return parsed.pathname + parsed.search;
    } catch (e) {
      return null;
    }
  }

  function extractSectionHtml(sectionsJson, sectionId) {
    if (!sectionsJson || !sectionsJson[sectionId]) return '';
    var doc = new DOMParser().parseFromString(sectionsJson[sectionId], 'text/html');
    var panel = doc.querySelector('[data-menu-drawer-panel], [data-mega-menu-products]');
    if (panel) return panel.innerHTML;
    var sectionEl = doc.querySelector('.shopify-section');
    return sectionEl ? sectionEl.innerHTML : doc.body.innerHTML;
  }

  function fetchSections(url) {
    return fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Section request failed');
        return res.json();
      })
      .catch(function () {
        return null;
      });
  }

  function bindDrawerSubmenus(drawer) {
    if (!drawer) return;
    var outerDetails = drawer.querySelector('details');
    var isTouch = false;
    window.addEventListener(
      'touchstart',
      function () {
        isTouch = true;
      },
      { once: true, passive: true }
    );

    function openSubmenu(details) {
      details.setAttribute('open', '');
      nextFrame(function () {
        details.classList.add('menu-opening');
      });
      var s = details.querySelector('summary');
      if (s) s.setAttribute('aria-expanded', 'true');
      details.querySelectorAll('[data-mega-products-mount]').forEach(loadMegaProductsMount);
    }

    function closeSubmenu(details) {
      details.classList.remove('menu-opening');
      details.removeAttribute('open');
      var s = details.querySelector('summary');
      if (s) s.setAttribute('aria-expanded', 'false');
    }

    drawer.querySelectorAll('details').forEach(function (details) {
      if (details === outerDetails) return;
      if (details.dataset.menuDeferBound === 'true') return;
      details.dataset.menuDeferBound = 'true';
      var summary = details.querySelector('summary');
      if (!summary) return;
      summary.addEventListener('click', function (e) {
        if (e.target.closest('.menu-drawer__item-link')) {
          e.stopImmediatePropagation();
          return;
        }
        e.preventDefault();
        e.stopImmediatePropagation();
        if (details.hasAttribute('open')) {
          closeSubmenu(details);
        } else {
          openSubmenu(details);
        }
      });
    });

    customElements.whenDefined('header-drawer').then(function () {
      var d = document.querySelector('header-drawer');
      if (!d || typeof d.closeMenuDrawer !== 'function' || d.dataset.menuDeferClosePatched === 'true') {
        return;
      }
      d.dataset.menuDeferClosePatched = 'true';
      var origClose = d.closeMenuDrawer.bind(d);
      d.closeMenuDrawer = function (event, elementToFocus) {
        if (
          event === true &&
          isTouch &&
          d.mainDetailsToggle &&
          d.mainDetailsToggle.querySelector('details[open]')
        ) {
          return;
        }
        origClose(event, elementToFocus);
      };
    });
  }

  function loadDrawerPanel() {
    if (drawerLoaded) return Promise.resolve(true);
    if (drawerLoading) return drawerLoading;

    var drawer = document.querySelector('header-drawer');
    var mount = drawer && drawer.querySelector('[data-menu-drawer-mount]');
    if (!mount) return Promise.resolve(false);

    mount.setAttribute('aria-busy', 'true');
    var url = withSectionsParam(routesRoot(), DRAWER_SECTION);
    drawerLoading = fetchSections(url).then(function (data) {
      var html = extractSectionHtml(data, DRAWER_SECTION);
      if (!html) {
        mount.setAttribute('aria-busy', 'false');
        drawerLoading = null;
        return false;
      }
      mount.innerHTML = html;
      mount.setAttribute('aria-busy', 'false');
      drawerLoaded = true;
      drawerLoading = null;

      // Bind MenuDrawer handlers only on injected nodes (avoid double-binding the shell summary).
      if (drawer && typeof drawer.onCloseButtonClick === 'function') {
        mount.querySelectorAll('button:not(.menu-drawer__close-menu-btn)').forEach(function (btn) {
          btn.addEventListener('click', drawer.onCloseButtonClick.bind(drawer));
        });
      }
      bindDrawerSubmenus(drawer);
      return true;
    });

    return drawerLoading;
  }

  function productListItemsHtml(sectionHtml) {
    var doc = new DOMParser().parseFromString('<div>' + sectionHtml + '</div>', 'text/html');
    var list = doc.querySelector('[data-mega-menu-products] ul, ul.products-mega-menu__cards, ul');
    if (!list) return sectionHtml;
    return list.innerHTML;
  }

  function prependFetchedProducts(mount, itemsHtml) {
    if (!itemsHtml) return;
    var tmp = document.createElement('ul');
    tmp.innerHTML = itemsHtml;
    var firstExisting = mount.firstChild;
    while (tmp.firstChild) {
      mount.insertBefore(tmp.firstChild, firstExisting);
    }
  }

  function loadMegaProductsMount(mount) {
    if (!mount || mount.dataset.loaded === 'true' || mount.dataset.loading === 'true') return;
    var collectionUrl = mount.getAttribute('data-collection-url');
    if (!collectionUrl || collectionUrl.indexOf('/collections/') === -1) return;

    var url = withSectionsParam(collectionUrl, PRODUCTS_SECTION);
    if (!url) return;

    if (productsCache[url]) {
      prependFetchedProducts(mount, productsCache[url]);
      mount.dataset.loaded = 'true';
      return;
    }

    mount.dataset.loading = 'true';
    mount.setAttribute('aria-busy', 'true');
    fetchSections(url).then(function (data) {
      var html = extractSectionHtml(data, PRODUCTS_SECTION);
      mount.dataset.loading = 'false';
      mount.setAttribute('aria-busy', 'false');
      if (!html) return;
      var itemsHtml = productListItemsHtml(html);
      productsCache[url] = itemsHtml;
      prependFetchedProducts(mount, itemsHtml);
      mount.dataset.loaded = 'true';
    });
  }

  function loadVisibleMegaProducts(root) {
    (root || document).querySelectorAll('[data-mega-products-mount]').forEach(loadMegaProductsMount);
  }

  function patchHeaderDrawerOpen() {
    customElements.whenDefined('header-drawer').then(function () {
      var drawer = document.querySelector('header-drawer');
      if (!drawer || typeof drawer.openMenuDrawer !== 'function') return;
      if (drawer.dataset.menuDeferOpenPatched === 'true') return;
      drawer.dataset.menuDeferOpenPatched = 'true';

      var origOpen = drawer.openMenuDrawer.bind(drawer);
      drawer.openMenuDrawer = function (summary) {
        var self = this;
        loadDrawerPanel().then(function () {
          nextFrame(function () {
            if (!self.mainDetailsToggle || !self.mainDetailsToggle.hasAttribute('open')) return;
            origOpen.call(self, summary);
          });
        });
      };

      var summary = drawer.querySelector('summary.header__icon--menu');
      if (summary) {
        summary.addEventListener('pointerdown', function () {
          loadDrawerPanel();
        });
        summary.addEventListener('focus', function () {
          loadDrawerPanel();
        });
      }
    });
  }

  function patchProductsMegaMenu() {
    customElements.whenDefined('products-mega-menu').then(function () {
      var Proto = customElements.get('products-mega-menu');
      if (!Proto || !Proto.prototype || Proto.prototype.__menuDeferPatched) return;
      Proto.prototype.__menuDeferPatched = true;

      var origOpen = Proto.prototype.openMenu;
      Proto.prototype.openMenu = function () {
        if (typeof origOpen === 'function') origOpen.call(this);
        loadVisibleMegaProducts(this);
      };

      var origDisplay = Proto.prototype.displayContent;
      Proto.prototype.displayContent = function (event) {
        if (typeof origDisplay === 'function') origDisplay.call(this, event);
        var item = event && event.target && event.target.closest('[id^="ProductsMegaMenu-Item"]');
        if (item) loadVisibleMegaProducts(item);
      };
    });
  }

  function init() {
    patchHeaderDrawerOpen();
    patchProductsMegaMenu();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

